import { SALES_TEAMS } from 'utils/slug';
import { ISeller, ISellerTeamGoal } from './types';

export const GOAL_MAX = 10000000;

/** El formulario: el equipo como se escribe y el objetivo como texto (vacío = sin objetivo). */
export interface SellerForm {
	team: string;
	goal: string;
}

export const sellerToForm = (seller: ISeller): SellerForm => ({
	team: seller.team ?? '',
	goal: seller.goal === null || seller.goal === undefined ? '' : String(seller.goal),
});

/** El equipo sin espacios de más, como lo guarda la API. */
const cleanTeam = (team: string): string => team.trim().replace(/\s+/g, ' ');

/** Un equipo de los dashboards, con su grafía (`centro uy` → `CENTRO UY`): los dashboards comparan exacto. */
const dashboardSpelling = (team: string): string => SALES_TEAMS.find((known) => known === team.toUpperCase()) ?? team;

/**
 * Lo que se manda: el equipo sin espacios de más y, si es de los dashboards, con su grafía (vacío = sin equipo), y
 * el objetivo entero (vacío = sin objetivo).
 */
export const formToTeamGoal = (form: SellerForm): ISellerTeamGoal => {
	const team = cleanTeam(form.team);
	return {
		team: team === '' ? null : dashboardSpelling(team),
		goal: form.goal.trim() === '' ? null : Number(form.goal),
	};
};

/** Los mismos límites que la API. `null` si está bien. */
export function validateGoal(goal: string): string | null {
	if (goal.trim() === '') return null;
	if (!/^\d+$/.test(goal.trim())) return 'Un número entero de dólares, sin decimales';
	if (Number(goal) > GOAL_MAX) return 'Hasta 10.000.000';
	return null;
}

/** Los dashboards de ventas reconocen solo los equipos de una lista fija: uno nuevo no aparece ahí. */
export const isDashboardTeam = (team: string): boolean =>
	(SALES_TEAMS as readonly string[]).includes(cleanTeam(team).toUpperCase());

export const sameTeamGoal = (a: ISellerTeamGoal, b: ISellerTeamGoal): boolean => a.team === b.team && a.goal === b.goal;

/** El mensaje para el usuario según lo que respondió la API. */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const describeSellerWriteError = (error: any): string => {
	const status: number | undefined = error?.response?.status;
	const apiMessage: string | undefined = error?.response?.data?.message;
	if (status === 403) return 'Solo un administrador puede cambiar el equipo o el objetivo.';
	if (status === 404 && apiMessage && !apiMessage.startsWith('Not Found'))
		return 'Ese usuario ya no existe: actualizá la lista.';
	if (status === 404 || status === 405)
		return 'La API de sale orders todavía no tiene esta función: falta deployar la versión nueva.';
	if (status === 422 && apiMessage) return `La API rechazó los datos: ${apiMessage}`;
	return apiMessage || 'No se pudo guardar';
};

/** "USD 4.500", o "—" sin objetivo. */
export const formatGoal = (goal: number | null | undefined): string =>
	goal === null || goal === undefined ? '—' : `USD ${goal.toLocaleString('es-UY')}`;
